import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Layers, Loader2, Save, Send, Wand2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import AdminAnalysisReviewPanel from "@/components/admin/AdminAnalysisReviewPanel";
import AdminChordTimelineEditor from "@/components/admin/AdminChordTimelineEditor";
import AdminMusicCatalogWizard from "@/components/admin/AdminMusicCatalogWizard";
import AdminPracticePreview from "@/components/admin/AdminPracticePreview";
import AdminSyncOffsetEditor from "@/components/admin/AdminSyncOffsetEditor";
import AdminYouTubePicker from "@/components/admin/AdminYouTubePicker";
import SongStructurePanel from "@/components/SongStructurePanel";
import { normalizeChordChanges, normalizeChordEvent } from "@/music/chords/chordSyncUtils";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "min-h-11 w-full rounded-xl border border-border bg-ink/40 px-3 text-sm text-foreground outline-none focus:border-signal/45";
const EMPTY_SONG = { title: "", artist: "", genre: "", key: "", bpm: "", duration_seconds: "", status: "draft" };
const EMPTY_PROFILE = { key: "", bpm: "", genre: "", confidence: 0.8, timing_source: "manual", sync_offset_seconds: 0, chords: [], sections: [] };

const Field = ({ label, children }) => (
  <label className="space-y-1.5">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </label>
);

const AdminSongEditorPage = ({ create = false }) => {
  const { songId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [song, setSong] = useState(EMPTY_SONG);
  const [video, setVideo] = useState(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(!create);
  const [saving, setSaving] = useState(false);
  const [playback, setPlayback] = useState({ currentTime: 0, isPlaying: false, seekTo: null });
  const [structure, setStructure] = useState(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [stemsAnalyzing, setStemsAnalyzing] = useState(false);
  const [hasStemsCompleted, setHasStemsCompleted] = useState(false);
  const [chordComplexity, setChordComplexity] = useState("standard");
  const transcribePollingRef = useRef(null);
  const stemsPollingRef = useRef(null);
  const prevTranscriptionStatusRef = useRef(null);
  const prevStemsStatusRef = useRef(null);

  const loadSong = useCallback(async () => {
    if (create || !songId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/songs/${songId}`, { headers: getAuthHeaders() });
      const current = data.song;
      setSong({
        title: current.canonical_title || "",
        artist: current.canonical_artist || "",
        genre: current.genre || "",
        key: current.primary_key || "",
        bpm: current.primary_bpm || "",
        duration_seconds: current.duration_estimate || "",
        status: current.status || "draft",
      });
      setVideo((data.sources || []).find((item) => item.status !== "archived") || null);
      setHasStemsCompleted(data.has_completed_stems || false);
      const currentProfile = data.profiles?.[0];
      if (currentProfile) {
        const offset = Number(currentProfile.sync_offset_seconds || 0);
        setProfile({
          key: currentProfile.key || "",
          bpm: currentProfile.bpm || "",
          genre: currentProfile.genre || "",
          confidence: Number(currentProfile.confidence ?? 0.8),
          timing_source: currentProfile.timing_source || "manual",
          sync_offset_seconds: offset,
          chords: normalizeChordChanges(currentProfile.chords || [], current.duration_estimate).map((chord, index) =>
            normalizeChordEvent({ ...chord, start: Math.max(0, chord.start - offset), time: Math.max(0, chord.start - offset), end: Math.max(0, chord.end - offset) }, index)
          ),
          sections: currentProfile.sections || [],
        });
        setProfileId(currentProfile.id);
        setChordComplexity(currentProfile.chord_complexity || "standard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar la cancion");
    } finally {
      setLoading(false);
    }
  }, [create, getAuthHeaders, songId]);

  useEffect(() => { loadSong(); }, [loadSong]);

  // Load structure when song is available
  useEffect(() => {
    if (create || !songId) return;
    let cancelled = false;
    setStructureLoading(true);
    axios
      .get(`${API_URL}/global-songs/${songId}/structure`, { headers: getAuthHeaders() })
      .then((res) => { if (!cancelled) setStructure(res.data); })
      .catch(() => { if (!cancelled) setStructure(null); })
      .finally(() => { if (!cancelled) setStructureLoading(false); });
    return () => { cancelled = true; };
  }, [create, getAuthHeaders, songId]);

  // ── Poll song profiles every 15s for transcription_status ───────────────
  const stopTranscribePolling = useCallback(() => {
    if (transcribePollingRef.current) {
      clearInterval(transcribePollingRef.current);
      transcribePollingRef.current = null;
    }
  }, []);

  const pollTranscriptionStatus = useCallback(async () => {
    if (!songId) return;
    try {
      const { data } = await axios.get(`${API_URL}/admin/songs/${songId}`, { headers: getAuthHeaders() });
      const latestProfile = (data.profiles || [])[0];
      const status = latestProfile?.transcription_status;
      const prevStatus = prevTranscriptionStatusRef.current;
      prevTranscriptionStatusRef.current = status;

      if (status === "done" && prevStatus !== "done") {
        stopTranscribePolling();
        setTranscribing(false);
        toast.success("✅ Letra disponible — la estructura se actualizará en unos segundos.");
        // Reload structure
        setStructureLoading(true);
        axios
          .get(`${API_URL}/global-songs/${songId}/structure`, { headers: getAuthHeaders() })
          .then((res) => setStructure(res.data))
          .catch(() => {})
          .finally(() => setStructureLoading(false));
      } else if (status === "failed") {
        stopTranscribePolling();
        setTranscribing(false);
        const errDetail = latestProfile?.transcription_error || "Error desconocido";
        toast.error(`❌ Transcripción falló: ${errDetail}`);
      }
    } catch {
      // transient error — keep polling
    }
  }, [songId, getAuthHeaders, stopTranscribePolling]);

  // Clean up polling on unmount
  useEffect(() => () => { stopTranscribePolling(); if (stemsPollingRef.current) clearInterval(stemsPollingRef.current); }, [stopTranscribePolling]);

  // ── Stems analysis polling ───────────────────────────────────────────────
  const pollStemsStatus = useCallback(async () => {
    if (!songId) return;
    try {
      const { data } = await axios.get(`${API_URL}/admin/songs/${songId}`, { headers: getAuthHeaders() });
      const latestProfile = (data.profiles || [])[0];
      const status = latestProfile?.stem_analysis_status;
      const prev = prevStemsStatusRef.current;
      prevStemsStatusRef.current = status;
      if (status === "done" && prev !== "done") {
        if (stemsPollingRef.current) { clearInterval(stemsPollingRef.current); stemsPollingRef.current = null; }
        setStemsAnalyzing(false);
        toast.success("✅ Acordes actualizados con análisis de stems.");
        loadSong();
      } else if (status === "failed") {
        if (stemsPollingRef.current) { clearInterval(stemsPollingRef.current); stemsPollingRef.current = null; }
        setStemsAnalyzing(false);
        toast.error(`❌ Análisis de stems falló: ${latestProfile?.stem_analysis_error || "Error desconocido"}`);
      }
    } catch { /* transient error — keep polling */ }
  }, [songId, getAuthHeaders, loadSong]);

  const triggerStemAnalysis = async () => {
    if (!songId) return;
    setStemsAnalyzing(true);
    prevStemsStatusRef.current = null;
    try {
      await axios.post(`${API_URL}/admin/songs/${songId}/analyze-stems`, {}, { headers: getAuthHeaders() });
      toast.info("⏳ Análisis con stems iniciado — puede tardar 2-4 min.");
      if (stemsPollingRef.current) clearInterval(stemsPollingRef.current);
      stemsPollingRef.current = setInterval(pollStemsStatus, 15000);
    } catch (err) {
      setStemsAnalyzing(false);
      toast.error(err.response?.data?.detail || "No se pudo iniciar el análisis con stems.");
    }
  };

  const handleComplexityChange = async (newComplexity) => {
    setChordComplexity(newComplexity);
    try {
      await axios.patch(`${API_URL}/admin/songs/${songId}/simplify-chords`, { complexity: newComplexity }, { headers: getAuthHeaders() });
      toast.success(`Acordes simplificados a nivel "${newComplexity}"`);
      loadSong();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar la simplificación.");
    }
  };

  const triggerTranscription = async () => {
    if (!songId) return;
    setTranscribing(true);
    prevTranscriptionStatusRef.current = null;
    try {
      await axios.post(`${API_URL}/admin/songs/${songId}/transcribe`, {}, { headers: getAuthHeaders() });
      toast.info("⏳ Transcripción iniciada — puede tardar 3-8 minutos. Avisaremos cuando esté lista.");
      // Start polling every 15s
      stopTranscribePolling();
      transcribePollingRef.current = setInterval(pollTranscriptionStatus, 15000);
    } catch (err) {
      setTranscribing(false);
      toast.error(err.response?.data?.detail || "No se pudo iniciar la transcripción.");
    }
  };

  const updateSong = (key, value) => setSong((current) => ({ ...current, [key]: value }));
  const updateProfile = (key, value) => setProfile((current) => ({ ...current, [key]: value }));
  const effectiveChords = useMemo(
    () => normalizeChordChanges(profile.chords, song.duration_seconds).map((chord, index) =>
      normalizeChordEvent({
        ...chord,
        start: Math.max(0, chord.start + Number(profile.sync_offset_seconds || 0)),
        time: Math.max(0, chord.start + Number(profile.sync_offset_seconds || 0)),
        end: Math.max(0, chord.end + Number(profile.sync_offset_seconds || 0)),
      }, index)
    ),
    [profile.chords, profile.sync_offset_seconds, song.duration_seconds]
  );
  const activeStep = !songId ? 0 : !video ? 1 : !profile.chords.length ? 2 : Number(profile.sync_offset_seconds) === 0 ? 3 : 4;

  const saveSong = async () => {
    if (!song.title.trim()) {
      toast.error("Escribe el titulo de la cancion");
      return null;
    }
    setSaving(true);
    const payload = {
      title: song.title.trim(),
      artist: song.artist || null,
      genre: song.genre || null,
      key: song.key || null,
      bpm: song.bpm ? Number(song.bpm) : null,
      duration_seconds: song.duration_seconds ? Number(song.duration_seconds) : null,
    };
    try {
      if (create) {
        const { data } = await axios.post(`${API_URL}/admin/songs`, payload, { headers: getAuthHeaders() });
        toast.success("Borrador creado. Ahora elige el video.");
        navigate(`/admin/music-catalog/${data.id}/edit`, { replace: true });
        return data.id;
      }
      await axios.patch(`${API_URL}/admin/songs/${songId}`, payload, { headers: getAuthHeaders() });
      toast.success("Datos musicales guardados");
      return songId;
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar la cancion");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async ({ quiet = false } = {}) => {
    const payload = {
      global_song_id: songId,
      key: profile.key || song.key || null,
      bpm: profile.bpm ? Number(profile.bpm) : song.bpm ? Number(song.bpm) : null,
      genre: profile.genre || song.genre || null,
      confidence: Number(profile.confidence || 0),
      timing_source: profile.timing_source,
      sync_offset_seconds: Number(profile.sync_offset_seconds || 0),
      chords: effectiveChords,
      sections: profile.sections,
      lyrics_sections: [],
      status: "draft",
      visibility: "private",
    };
    try {
      if (profileId) {
        const { global_song_id, ...updates } = payload;
        await axios.patch(`${API_URL}/admin/analysis-profiles/${profileId}`, updates, { headers: getAuthHeaders() });
      } else {
        const { data } = await axios.post(`${API_URL}/admin/analysis-profiles`, payload, { headers: getAuthHeaders() });
        setProfileId(data.id);
      }
      if (!quiet) toast.success("Timeline guardada como borrador");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar la timeline");
      return false;
    }
  };

  const submit = async (action) => {
    const saved = await saveProfile({ quiet: true });
    if (!saved) return;
    try {
      await axios.post(`${API_URL}/admin/songs/${songId}/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(action === "publish" ? "Practica publicada" : "Lista para revision");
      loadSong();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar el estado");
    }
  };

  if (loading) return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-signal" /></div>;

  return (
    <section className="steel-page space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/music-catalog" className="grid h-11 w-11 place-items-center rounded-xl border border-border text-muted-foreground"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-signal/65">Music Catalog Studio</p>
            <h1 className="text-2xl font-bold text-foreground">{create ? "Crear practica" : song.title || "Editar practica"}</h1>
          </div>
        </div>
        {!create && <Badge className="bg-muted text-muted-foreground">{song.status}</Badge>}
      </header>
      <AdminMusicCatalogWizard activeStep={activeStep} />
      <section className="rounded-2xl border border-border bg-panel p-4 sm:p-5">
        <h2 className="mb-4 text-lg font-semibold text-foreground">1. Cancion</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Titulo"><input className={inputClass} value={song.title} onChange={(event) => updateSong("title", event.target.value)} /></Field>
          <Field label="Artista"><input className={inputClass} value={song.artist} onChange={(event) => updateSong("artist", event.target.value)} /></Field>
          <Field label="Genero"><input className={inputClass} value={song.genre} onChange={(event) => updateSong("genre", event.target.value)} /></Field>
          <Field label="Key"><input className={inputClass} value={song.key} onChange={(event) => updateSong("key", event.target.value)} /></Field>
          <Field label="BPM"><input type="number" className={inputClass} value={song.bpm} onChange={(event) => updateSong("bpm", event.target.value)} /></Field>
          <Field label="Duracion"><input type="number" className={inputClass} value={song.duration_seconds} onChange={(event) => updateSong("duration_seconds", event.target.value)} placeholder="segundos" /></Field>
        </div>
        <button type="button" onClick={saveSong} disabled={saving} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-signal px-5 text-sm font-semibold text-ink disabled:opacity-50"><Save className="h-4 w-4" /> {create ? "Continuar con video" : "Guardar cancion"}</button>
      </section>
      {!create && (
        <>
          <AdminYouTubePicker songId={songId} selectedVideo={video} onSelected={setVideo} />
          <AdminAnalysisReviewPanel profile={{ ...profile, chords: effectiveChords }} hasVideo={Boolean(video)} song={song} songId={songId} />
          <section className="grid gap-3 rounded-2xl border border-border bg-panel p-4 sm:grid-cols-4 sm:p-5">
            <Field label="Key"><input className={inputClass} value={profile.key} onChange={(event) => updateProfile("key", event.target.value)} /></Field>
            <Field label="BPM"><input type="number" className={inputClass} value={profile.bpm} onChange={(event) => updateProfile("bpm", event.target.value)} /></Field>
            <Field label="Confidence"><input type="number" min="0" max="1" step="0.01" className={inputClass} value={profile.confidence} onChange={(event) => updateProfile("confidence", event.target.value)} /></Field>
            <Field label="Timing">
              <select className={inputClass} value={profile.timing_source} onChange={(event) => updateProfile("timing_source", event.target.value)}>
                <option value="manual" className="bg-ink">Manual</option>
                <option value="real" className="bg-ink">Analisis autorizado</option>
                <option value="estimated" className="bg-ink">Estimado</option>
              </select>
            </Field>
          </section>
          <AdminPracticePreview video={video} chords={effectiveChords} duration={song.duration_seconds} bpm={profile.bpm || song.bpm} onSyncChange={setPlayback} />
          {/* Stems re-analysis + chord complexity */}
          <section className="rounded-2xl border border-border bg-panel p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground">Acordes</h2>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  Complejidad
                  <select
                    className="rounded-lg border border-border bg-ink/50 px-2 py-1.5 text-xs text-foreground"
                    value={chordComplexity}
                    onChange={(e) => handleComplexityChange(e.target.value)}
                    disabled={!profileId}
                  >
                    <option value="basic">Básico (mayor/menor)</option>
                    <option value="standard">Estándar (+ 7, dim)</option>
                    <option value="extended">Extendido (+ maj7, m7, aug)</option>
                    <option value="full">Completo (sin filtro)</option>
                  </select>
                </label>
                {hasStemsCompleted && (
                  <button
                    type="button"
                    onClick={triggerStemAnalysis}
                    disabled={stemsAnalyzing}
                    className="inline-flex items-center gap-2 rounded-xl border border-tune/25 px-4 py-2 text-sm font-semibold text-tune hover:bg-tune/10 disabled:opacity-50"
                  >
                    {stemsAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
                    {stemsAnalyzing ? "Analizando stems…" : "Re-analizar con stems"}
                  </button>
                )}
              </div>
            </div>
          </section>
          <AdminChordTimelineEditor
            chords={profile.chords}
            onChange={(chords) => updateProfile("chords", chords)}
            duration={song.duration_seconds}
            bpm={profile.bpm || song.bpm}
            musicalKey={profile.key || song.key}
            currentTime={Math.max(0, playback.currentTime - Number(profile.sync_offset_seconds || 0))}
            isPlaying={playback.isPlaying}
            onSeek={(time) => playback.seekTo?.(Math.max(0, time + Number(profile.sync_offset_seconds || 0)))}
            onTogglePlayback={() => playback.togglePlayback?.()}
          />
          <AdminSyncOffsetEditor value={profile.sync_offset_seconds} onChange={(value) => updateProfile("sync_offset_seconds", value)} />

          {/* Letra y Estructura */}
          <section className="rounded-2xl border border-border bg-panel p-4 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-foreground">Letra y Estructura</h2>
                {transcribing && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-brass/15 px-2.5 py-0.5 text-xs font-semibold text-brass">
                    <Loader2 className="h-3 w-3 animate-spin" /> Transcribiendo…
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={triggerTranscription}
                disabled={transcribing}
                className="inline-flex items-center gap-2 rounded-xl border border-brass/25 px-4 py-2 text-sm font-semibold text-brass hover:bg-brass/10 disabled:opacity-50"
              >
                {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {transcribing ? "Transcribiendo…" : "Re-transcribir con Whisper"}
              </button>
            </div>
            <SongStructurePanel
              structure={structure}
              currentTime={Math.max(0, playback.currentTime - Number(profile.sync_offset_seconds || 0))}
              onSectionClick={(t) => playback.seekTo?.(Math.max(0, t + Number(profile.sync_offset_seconds || 0)))}
              loading={structureLoading}
            />
          </section>

          <footer className="flex flex-wrap justify-end gap-2 rounded-2xl border border-border bg-panel p-4">
            <button type="button" onClick={() => saveProfile()} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-signal/30 px-5 text-sm font-semibold text-signal"><Save className="h-4 w-4" /> Guardar borrador</button>
            <button type="button" onClick={() => submit("review")} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-brass/30 px-5 text-sm font-semibold text-brass"><Send className="h-4 w-4" /> Enviar a revision</button>
            <button type="button" onClick={() => submit("publish")} disabled={!video || !effectiveChords.length} className="min-h-11 rounded-xl bg-tune/20 px-5 text-sm font-semibold text-tune disabled:opacity-40">Publicar practica</button>
          </footer>
        </>
      )}
    </section>
  );
};

export default AdminSongEditorPage;
